<template>
  <div v-if="!isLoading" class="app-container">
    <template v-if="isPublicRoute">
      <!-- Public route — clean layout, no sidebar/topbar, works with or without auth -->
      <router-view />
    </template>

    <template v-else-if="isAuthenticated">
      <!-- Top Bar -->
      <TopBar class="app-topbar" />
      <GlobalBanners />

      <!-- Mobile Backdrop Overlay -->
      <div
        v-if="isMobile && isSidebarOpen"
        class="sidebar-backdrop"
        @click="closeSidebar"
      ></div>

      <!-- Main Content with Sidebar -->
      <div class="app-body" :class="{ 'full-screen': route.meta?.fullScreen }">
        <Sidebar class="app-sidebar" />

        <!-- Page Content -->
        <main class="app-main" :class="{ 'sidebar-open': isSidebarOpen && !isMobile, 'full-screen': route.meta?.fullScreen }">
          <router-view />
          <footer v-if="!route.meta?.fullScreen" class="app-version-footer">{{ appVersion }}</footer>
        </main>
      </div>
    </template>

    <template v-else>
      <!-- Not authenticated - redirect -->
      <div class="auth-redirect">
        <h2>🔒 Not Authenticated</h2>
        <p>Redirecting to login...</p>
      </div>
    </template>
  </div>
  
  <div v-else class="loading-screen">
    <h2>⏳ Loading...</h2>
    <p>Checking authentication...</p>
  </div>
  
  <!-- Global Components -->
  <LoadingProgressBar v-if="!isPublicRoute" />
  <GlobalMessages />
  <ConfirmDialog v-if="!isPublicRoute" />

  <!-- Centralized Modal Manager (handles all modals via eventBus) -->
  <ModalManager v-if="isAuthenticated && !isPublicRoute" />

  <!-- Floating Support Ticket Button (visible on all authenticated pages) -->
  <FloatingTicketButton v-if="isAuthenticated && !isPublicRoute" />

  <!-- Floating SO Search Button (visible on all authenticated pages) -->
  <FloatingSearchButton v-if="isAuthenticated" />

  <!-- JWT Debug Panel (only in development) -->
  <JwtDebugPanel v-if="isDevelopment && isAuthenticated && !isPublicRoute" />
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TopBar from './ui/TopBar.vue'
import GlobalBanners from './ui/GlobalBanners.vue'
import Sidebar from './ui/Sidebar.vue'
import LoadingProgressBar from './ui/LoadingProgressBar.vue'
import GlobalMessages from './ui/GlobalMessages.vue'
import ModalManager from './components/ModalManager.vue'
import FloatingTicketButton from './components/FloatingTicketButton.vue'
import FloatingSearchButton from './components/FloatingSearchButton.vue'
import { useSidebar } from './composables/useSidebar'
import { useFiltro } from './composables/useFiltro'
import { useSubscriptionStatus, resolveLockDestination, LOCK_EXEMPT_ROUTE_NAMES, billingUiEnabled } from './composables/useSubscriptionStatus'
import { useTutorial } from './composables/useTutorial'
import { useKeyboardShortcuts } from './composables/useKeyboardShortcuts'
import { log } from './utils/DebugLogger'
import { httpService } from './services/httpService'
import { config } from './services/configService'
import { permissionsState } from './services/permissionsService'
import type { Subscriptions_MyFranchisesResponseDados } from './contracts/Subscriptions_MyFranchisesResponse'
// Side-effect import: registers the contract with the responseContract registry.
import './contracts/Subscriptions_MyFranchisesResponse'

// Get permissionsService (reactive)
const permissionsService = inject<any>('permissionsService')

// Get current route + router for programmatic navigation
const route = useRoute()
const router = useRouter()

// Sidebar state
const { isSidebarOpen, isMobile, closeSidebar } = useSidebar()

// Get filtro composable (now available in Vue context)
const filtro = useFiltro()
const subscriptionStatus = useSubscriptionStatus()

// Tutorial system (auto-triggers on route changes)
const tutorial = useTutorial()

// Global keyboard shortcuts (Ctrl/Cmd+. for support ticket, etc.)
const keyboardShortcuts = useKeyboardShortcuts()

// Computed properties
const isAuthenticated = computed((): boolean => {
  const currentPerms = permissionsService?.getPermissionsData()
  return !!(currentPerms && currentPerms.user)
})

const isPublicRoute = computed((): boolean => {
  return route.meta?.requiresAuth === false
})

const isLoading = ref(false) // Already loaded in main.ts bootstrap

// Development mode check
const isDevelopment: boolean = config.isDevelopment

// Build version tag — injected by CI as VITE_APP_VERSION (git tag) at Vite build time
const appVersion: string = config.app.appVersion

onMounted(async () => {
  log('App', 'onMounted', 'Vue SPA mounted successfully!')
  log('App', 'onMounted', 'Auth status:', {
    isAuthenticated: isAuthenticated.value,
    isPublicRoute: isPublicRoute.value,
    user: permissionsService?.getPermissionsData()?.user
  })

  // Initialize global keyboard shortcuts
  keyboardShortcuts.initGlobalShortcuts()
  log('App', 'onMounted', '⌨️ Keyboard shortcuts initialized (Ctrl/Cmd+. for support)')

  // Initialize filter data (now that Vue context is available)
  if (isAuthenticated.value) {
    try {
      log('App', 'onMounted', 'Initializing filter data...')
      await filtro.init()
      log('App', 'onMounted', '✅ Filter initialized successfully')
    } catch (filtroError) {
      log('App', 'onMounted', '⚠️ Filter initialization failed:', filtroError)
      // Non-critical - app can still function
    }

    try {
      // Billing desligado no tenant (feature `pagamentoeuso` segue o
      // kill-switch AWRENV_SUBSCRIPTION_GATE via registro backend): nada de
      // cobrança existe na UI — sem fetch de my-franchises, sem banners de
      // setup/carência/inativa, sem lock. Os links de sidebar já somem pelo
      // registro de features (pagamentoeuso, staffsubscriptions, …).
      const billingEnabled = billingUiEnabled()
      const ownedIds = billingEnabled
        ? Object.keys(permissionsState.franquias).map(Number).filter(Boolean)
        : []
      if (ownedIds.length > 0) {
        const raw = await httpService.get(
          `${config.api.sistemaHost}/subscriptions/my-franchises`,
          { responseContract: 'Subscriptions_MyFranchisesResponse', skipErrorDisplay: true },
        )
        const resp = raw as { dados: Subscriptions_MyFranchisesResponseDados }
        const myRows = resp.dados.rows
        // Collect ALL rows and derive the banner from D4 fields — no second
        // HTTP call to subscriptions/status/{id} (Full-only, inaccessible to
        // franqueados). refreshFromRows reads franchise_name/status/days_remaining
        // already carried by my-franchises.
        subscriptionStatus.refreshFromRows(myRows)

        // Fase A — franquia sem assinatura (decisão do usuário, 2026-08-12):
        // lock duro. refreshFromRows() acima já atualizou allFranchisesInactive
        // de forma síncrona; se TODAS as franquias acessíveis estão sem
        // assinatura viva e a rota atual não é a própria superfície de
        // recuperação, redireciona por grupo. Substitui o antigo redirect
        // once-per-session (D6, removido) — o lock cobre o mesmo caso (e mais)
        // sem depender de flag de sessão. Mesma regra do guard do router
        // (LOCK_EXEMPT_ROUTE_NAMES / resolveLockDestination), para não
        // duplicar a decisão em 2 lugares.
        if (
          subscriptionStatus.allFranchisesInactive.value &&
          !LOCK_EXEMPT_ROUTE_NAMES.has(String(route.name))
        ) {
          const destination = resolveLockDestination(permissionsState.user?.groups)
          log('App', 'onMounted', 'Lock: franquia sem assinatura ativa — redirecionando', { destination })
          void router.replace(destination)
        }
      }
    } catch (subscriptionError) {
      log('App', 'onMounted', 'Subscription status check failed (non-critical)', subscriptionError)
    }
  }
})

onBeforeUnmount(() => {
  // Cleanup keyboard shortcuts
  keyboardShortcuts.cleanupGlobalShortcuts()
})
</script>

<style scoped>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-topbar {
  flex: 0 0 auto;
}

.app-body {
  display: flex;
  margin-top: 60px; /* Account for fixed topbar */
  position: relative;
  width: 100%;
  min-height: calc(100vh - 60px);
}

.app-sidebar {
  flex: 0 0 auto;
}

.app-main {
  flex: 1;
  padding: 20px;
  background-color: var(--app-bg-light);
  overflow-x: hidden; /* Prevent horizontal overflow */
  min-height: calc(100vh - 60px);
  /* P5: aligned with sidebar's width transition (0.25s) for a single coordinated reflow. */
  transition: all 0.25s ease;
  box-sizing: border-box;
  width: 0; /* Force flex to calculate width properly */
  min-width: 0; /* Allow content to shrink */
}

/* Full-screen routes (e.g. WhatsApp Inbox) — lock body + main to viewport, no scroll */
.app-body.full-screen {
  height: calc(100vh - 60px);
  overflow: clip; /* 'clip' blocks programmatic scrollIntoView unlike 'hidden' */
}

.app-main.full-screen {
  padding: 0;
  overflow: hidden;
  height: 100%; /* fills app-body.full-screen exactly */
}

.app-version-footer {
  text-align: center;
  padding: 8px 0 2px;
  font-size: 11px;
  color: #c3c9cf;
  user-select: none;
}

/* Desktop: Sidebar takes fixed width, main takes remaining */
/* No additional styles needed - flexbox handles the layout automatically */

/* Mobile Backdrop Overlay */
.sidebar-backdrop {
  position: fixed;
  top: 60px; /* Below topbar */
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 899; /* Below sidebar (900) but above content */
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.auth-redirect,
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  background-color: var(--app-bg-light);
}

.auth-redirect h2,
.loading-screen h2 {
  color: var(--app-text-color);
  margin-bottom: 16px;
}

.auth-redirect p,
.loading-screen p {
  color: var(--app-text-secondary-color);
  font-size: 16px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .app-main {
    padding: 16px;
    width: 100% !important; /* Full width on mobile */
    flex: 1 1 100%; /* Take full available space */
  }
}

/* Tablet */
@media (min-width: 769px) and (max-width: 1024px) {
  .app-main {
    padding: 18px;
  }
}

/* Small mobile */
@media (max-width: 480px) {
  .app-main {
    padding: 12px;
  }
}
</style>
